/** /preflight-user-groups — assignment groups (one per owner role) + membership editor.
 *
 * A small fixed set (one group per OwnerRole). Read is view-gated; editing membership
 * is manage-gated and system-users only (enforced server-side). Each new TO's items are
 * routed to the group matching their owner role, and members get a summary email. */
import GroupIcon from '@mui/icons-material/Group';
import { Box, Button, Chip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';

import { userGroups, type PreflightUserGroup } from '@/api/preflight';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { PreflightUserGroupMembersDialog } from './PreflightUserGroupMembersDialog';

function membersPreview(g: PreflightUserGroup): string {
  if (g.member_count === 0) return '—';
  const names = g.members.slice(0, 4).map((m) => m.name || m.email);
  const extra = g.member_count - names.length;
  return extra > 0 ? `${names.join(', ')} +${extra} more` : names.join(', ');
}

export function PreflightUserGroupsPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_preflight_manage');
  const [editing, setEditing] = useState<PreflightUserGroup | null>(null);

  const openEdit = useCallback((g: PreflightUserGroup) => setEditing(g), []);

  const columns = useMemo<GridColDef<PreflightUserGroup>[]>(
    () => [
      { field: 'name', headerName: 'Group', width: 200 },
      {
        field: 'owner_role',
        headerName: 'Role',
        width: 150,
        sortable: false,
        renderCell: (p) => <Chip size="small" label={p.row.owner_role} />,
      },
      {
        field: 'members',
        headerName: 'Members',
        flex: 1,
        minWidth: 260,
        sortable: false,
        valueGetter: (_, row) => membersPreview(row),
      },
      { field: 'member_count', headerName: '#', width: 80, sortable: false },
      ...(canManage
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 160,
              sortable: false,
              filterable: false,
              renderCell: (p: { row: PreflightUserGroup }) => (
                <Button
                  size="small"
                  startIcon={<GroupIcon fontSize="small" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(p.row);
                  }}
                >
                  Edit members
                </Button>
              ),
            } as GridColDef<PreflightUserGroup>,
          ]
        : []),
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      userGroups.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'name',
      }),
    [],
  );

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="preflight-user-groups"
        title="Preflight User Groups"
        subtitle="Assignment targets for onboarding items — one per owner role. Members are system users; each new TO's items are routed to the matching group."
      />

      <ResourceDataGrid<PreflightUserGroup>
        queryKey={['preflight', 'user-groups']}
        queryFn={queryFn}
        columns={columns}
        defaultSort={{ field: 'name', sort: 'asc' }}
        emptyMessage="No preflight user groups."
        headerToolbar={{ exportFileName: 'preflight-user-groups' }}
      />

      <PreflightUserGroupMembersDialog
        open={editing !== null}
        group={editing}
        onClose={() => setEditing(null)}
      />
    </Box>
  );
}
